import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { UntilDestroy } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'app-pencil-lazy-loading',
  templateUrl: './pencil-lazy-loading.component.html',
  styleUrls: ['./pencil-lazy-loading.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule],
})
export class PencilLazyLoadingComponent implements AfterViewInit, OnDestroy, OnChanges {
  @ViewChild('earlyLoadingAnchor', { read: ElementRef })
  earlyLoadingAnchor!: ElementRef<HTMLElement>;
  @ViewChild('lazyLoadingAnchor', { read: ElementRef }) lazyLoadingAnchor!: ElementRef<HTMLElement>;

  @Input() isItemsLoading: boolean = false;
  @Input() stopObserving: boolean = false;
  @Output() fetchMoreDataEvent = new EventEmitter<void>();

  private observer?: IntersectionObserver;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isItemsLoading && !changes.isItemsLoading.firstChange) {
      if (this.isItemsLoading) {
        this.disconnectObserver();
      } else {
        this.connectObserver();
      }
    }

    if (changes.stopObserving && this.stopObserving) {
      this.disconnectObserver();
    }
  }

  ngAfterViewInit(): void {
    this.observer = new IntersectionObserver((entries) => {
      if (entries.length === 0) {
        return;
      }

      for (const entry of entries) {
        if (entry.isIntersecting) {
          this.fetchMoreData();
          break; // if both anchors are intersecting, we only want to trigger the fetchMoreData once
        }
      }
    });

    this.connectObserver();
  }

  ngOnDestroy(): void {
    this.disconnectObserver();
  }

  disconnectObserver() {
    this.observer?.disconnect();
  }

  connectObserver() {
    this.observer?.observe(this.earlyLoadingAnchor.nativeElement);
    this.observer?.observe(this.lazyLoadingAnchor.nativeElement);
  }

  fetchMoreData() {
    if (this.isItemsLoading) {
      return;
    }

    this.disconnectObserver();
    this.fetchMoreDataEvent.emit();
  }
}

/**
 * A component for loading data as the user scrolls. Here's how it works:
 * 1. Wrap your content inside this component
 * 2. The component adds a hidden marker at the bottom
 * 3. When the user scrolls and sees this marker, it tells the parent to load more data
 *
 * Important usage notes:
 * - Set isItemsLoading to true before loading/adding new data, and false when done
 * - This helps handle edge cases where the marker stays visible after first load
 * - The component will pause and resume watching for scrolls based on this flag
 *
 * EDGE CASE that we solve : if we didn't disconnect and re connect the intersection observer ,
 * after loading the initial data if the anchor is intersecting it will trigger loading first batch of data.
 * but after that if the anchor still intersecting since the state of intersection didn't change it will not
 * trigger loading more data.
 *
 * For non-async data loading:
 * - Split the loading states into separate updates
 * - First set loading to true
 * - Add your new data
 * - Then use modifiedSetTimeout to set loading to false:
 *   modifiedSetTimeout(() => {
 *     this.isLoadingMoreSections$.next(false);
 *   });
 * if adding more data is done async then there is no need to do so.
 *
 * Use stopObserving=true when there's no more data to load.
 *
 * Note : i noticed through testing that the root margin property -that we should use to trigger the loading
 * a bit earlier than when the bottom is reached to have a smoother scroll experience- is not working as expected
 * in some cases. so instead i moved the anchor to the top by 500px. assuming  this compoonent will be used
 * for vertical scrolling and new data is appended to the bottom. we can generalize more once needed.
 */
